import { BrowserRouter as Router, Routes, Route, Link, useLocation, useNavigate } from 'react-router-dom';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import karaokeData from './data/karaokeData.json';
import channelInfo from './data/channelInfo.json';
import latestVideos from './data/latestVideos.json';
import './App.css';

const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

const missionBullets = [
  'Këndim në shtëpi me miqtë dhe familjen',
  'Ushtrime vokale për artistë dhe nxënës',
  'Mbremje karaoke në lokale, ahengje e festa private',
  'Argëtim kreativ për studio, podcast ose kontent social',
];

const commitments = [
  'Respektojmë të gjitha të drejtat e autorit – nuk monetizojmë asnjë publikim në YouTube.',
  'Çdo këngë lidhet drejtpërdrejt me videot origjinale në YouTube për transparencë.',
  'Përditësojmë playlistën KENDO.AL sa herë që publikohet një karaoke e re shqiptare.',
];

const useViewportWidth = () => {
  const [width, setWidth] = useState(() => (typeof window !== 'undefined' ? window.innerWidth : 1280));

  useEffect(() => {
    const handleResize = () => {
      setWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return width;
};

const formatNumber = (value = 0) => new Intl.NumberFormat('sq-AL').format(value);

const SongPlayer = ({ videoId, title, isActive, ytReady, onBlocked }) => {
  const containerRef = useRef(null);
  const playerRef = useRef(null);
  const placeholderRef = useRef(null);

  useEffect(() => {
    if (!isActive && playerRef.current) {
      playerRef.current.destroy();
      playerRef.current = null;
    }
    if (!isActive && placeholderRef.current) {
      placeholderRef.current.remove();
      placeholderRef.current = null;
    }
  }, [isActive]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    if (!isActive || !ytReady || !containerRef.current || playerRef.current) {
      return;
    }

    if (!(window.YT && window.YT.Player)) {
      return;
    }

    const mountNode = document.createElement('div');
    mountNode.className = 'song-card__player-slot';
    containerRef.current.innerHTML = '';
    containerRef.current.appendChild(mountNode);
    placeholderRef.current = mountNode;

    const origin = window?.location?.origin;

    playerRef.current = new window.YT.Player(mountNode, {
      videoId,
      width: '100%',
      height: '100%',
      playerVars: {
        rel: 0,
        modestbranding: 1,
        origin,
      },
      events: {
        onError: (event) => {
          if (event?.data === 101 || event?.data === 150) {
            onBlocked();
          }
        },
      },
    });

    return () => {
      if (playerRef.current) {
        playerRef.current.destroy();
        playerRef.current = null;
      }
      if (placeholderRef.current) {
        placeholderRef.current.remove();
        placeholderRef.current = null;
      }
    };
  }, [isActive, videoId, ytReady, onBlocked]);

  if (!isActive) {
    return null;
  }

  if (!ytReady) {
    return <div className="song-card__loading">Duke hapur lojtarin…</div>;
  }

  return (
    <div className="song-card__player-frame" title={`${title} karaoke`}>
      <div ref={containerRef} className="song-card__player-container" />
    </div>
  );
};

function App() {
  return (
    <Router>
      <SiteShell />
    </Router>
  );
}

function SiteShell() {
  const location = useLocation();
  const navigate = useNavigate();
  const [showScrollTop, setShowScrollTop] = useState(false);

  const totalSongs = karaokeData.length;
  const totalArtists = new Set(karaokeData.map((track) => track.singer)).size;
  const totalViews = channelInfo.viewCount ?? 0;

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, [location.pathname]);

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 400);
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const goToCatalog = useCallback(() => {
    navigate('/katalogu');
    requestAnimationFrame(() => window.scrollTo({ top: 0, behavior: 'smooth' }));
  }, [navigate]);

  const isActive = (path) => location.pathname === path;

  return (
    <div className="App">
      <header className="site-header">
        <Link to="/" className="brand-mark">
          <img src="/kendoal-logo.jpg" alt="KendoAL" className="brand-logo" />
          <div className="brand-text">
            <span className="brand-title">Kendo.al</span>
            <span className="brand-tagline">Karaoke Shqip</span>
          </div>
        </Link>
        <nav className="site-nav">
          <Link to="/" className={isActive('/') ? 'nav-link is-active' : 'nav-link'}>
            Kryefaqja
          </Link>
          <Link to="/katalogu" className={isActive('/katalogu') ? 'nav-link is-active' : 'nav-link'}>
            Katalogu
          </Link>
          <Link to="/disclaimer" className={isActive('/disclaimer') ? 'nav-link is-active' : 'nav-link'}>
            Klauzola
          </Link>
          <a className="nav-link" href="https://www.youtube.com/@Kendo_AL" target="_blank" rel="noreferrer">
            Kanali YouTube
          </a>
        </nav>
      </header>

      <Routes>
        <Route
          path="/"
          element={
            <HomeView
              karaokeData={karaokeData}
              totalSongs={totalSongs}
              totalArtists={totalArtists}
              totalViews={totalViews}
              onExplore={goToCatalog}
            />
          }
        />
        <Route
          path="/katalogu"
          element={<CatalogView karaokeData={karaokeData} totalSongs={totalSongs} totalArtists={totalArtists} />}
        />
        <Route path="/disclaimer" element={<DisclaimerPage />} />
      </Routes>


      <footer className="site-footer">
        <div>
          <p>© {new Date().getFullYear()} KENDO.AL · Platformë karaoke në gjuhën shqipe.</p>
          <p>
            Videot janë nga YouTube. Të drejtat u përkasin mbajtësve përkatës.{' '}
            <Link to="/disclaimer">Lexo klauzolën.</Link>
          </p>
          <p>
            Bazohet në playlistën zyrtare{' '}
            <a href="https://www.youtube.com/playlist?list=PL8z_vY1mD8J68ymnyb2QyQLJxcVG8qNe7" target="_blank" rel="noreferrer">
              KARAOKE SHQIP
            </a>{' '}
            dhe kanalit{' '}
            <a href="https://www.youtube.com/@Kendo_AL" target="_blank" rel="noreferrer">
              @Kendo_AL
            </a>
            .
          </p>
        </div>
      </footer>

      {showScrollTop && (
        <button type="button" className="scroll-top" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
          Kthehu lart
        </button>
      )}
    </div>
  );
}

function HomeView({ karaokeData, totalSongs, totalArtists, totalViews, onExplore }) {
  const latest = useMemo(() => {
    return latestVideos.slice(0, 10).map((video) => {
      const fallback = karaokeData.find((track) => track.videoId === video.videoId);
      return {
        ...fallback,
        ...video,
        url: video.url || fallback?.url || `https://www.youtube.com/watch?v=${video.videoId}`,
        singer: video.singer || fallback?.singer || '',
        song: video.song || fallback?.song || video.title,
      };
    });
  }, [karaokeData]);

  return (
    <main className="home">
      <section className="hero">
        <div className="hero__content">
          <p className="hero__eyebrow">Platformë Karaoke Shqiptare</p>
          <h1>Karaoke shqiptare për çdo skenë.</h1>
          <p className="hero__subline">
            KENDO.AL kuron çdo javë karaoke shqip – nga hitet e rinisë deri te klasiket popullore. Gati për të ndezur
            atmosferën në shtëpi, në studio ose në skenë.
          </p>
          <div className="hero__actions">
            <button type="button" className="cta" onClick={onExplore}>
              Hap katalogun
            </button>
            <a className="ghost" href="https://www.youtube.com/watch?v=0ns4a81-xl8" target="_blank" rel="noreferrer">
              Shiko videon e fundit
            </a>
          </div>
          <div className="hero__stats">
            <div>
              <span className="hero__stats-label">Këngëtarë</span>
              <strong>{formatNumber(totalArtists)}</strong>
            </div>
            <div>
              <span className="hero__stats-label">Këngë karaoke</span>
              <strong>{formatNumber(totalSongs)}</strong>
            </div>
            <div>
              <span className="hero__stats-label">Shikime totale</span>
              <strong>{`${formatNumber(totalViews)}+`}</strong>
            </div>
          </div>
        </div>
      </section>


      <section className="channel-info">
        <div>
          <p className="section-eyebrow">Rreth kanalit</p>
          <h2>KendoAL është prodhuar si një studio virtuale karaoke me fokus 100% shqiptar.</h2>
          <p>
            Çdo video në playlistën <strong>KARAOKE SHQIP</strong> përgatitet për të respektuar autorët origjinalë, duke
            siguruar që asnjë publikim të mos monetizohet. Për ju, kjo do të thotë akses i hapur në instrumentale të
            pastra, tekste të sakta dhe video të formatuara për çdo ekran.
          </p>
        </div>
        <div className="info-panels">
          <article>
            <h3>Për çfarë përdoret?</h3>
            <ul>
              {missionBullets.map((point) => (
                <li key={point}>{point}</li>
              ))}
            </ul>
          </article>
          <article>
            <h3>Si punojmë?</h3>
            <ul>
              {commitments.map((point) => (
                <li key={point}>{point}</li>
              ))}
            </ul>
          </article>
        </div>
      </section>

      <HomeFeed songs={latest} onExplore={onExplore} />

      <section className="playlist-callout">
        <div>
          <p className="section-eyebrow">Playlist zyrtare</p>
          <h2>Përditësim i rregullt për çdo këngë karaoke shqip.</h2>
          <p>
            Nga videoja “Besa – Fishekzjarre” e deri te bashkëpunimet Majk, Noizy, Dafina dhe artistë klasikë, playlist-a
            përditësohet sapo publikohet një version i ri karaoke. Ndiq lidhjen më poshtë për të ruajtur koleksionin.
          </p>
          <div className="hero__actions">
            <a
              className="cta"
              href="https://www.youtube.com/playlist?list=PL8z_vY1mD8J68ymnyb2QyQLJxcVG8qNe7"
              target="_blank"
              rel="noreferrer"
            >
              Hap playlistën KARAOKE SHQIP
            </a>
            <button type="button" className="ghost" onClick={onExplore}>
              Shfleto këngët këtu
            </button>
          </div>
        </div>
        <div className="playlist-card">
          <p>🎵 110 këngë • Përditësohet vazhdimisht</p>
          <h3>KendoAL · Karaoke Shqip</h3>
          <p>
            “Ky kanal nuk monetizohet për të respektuar të drejtat e autorit. Të gjitha të drejtat i përkasin autorëve dhe
            shtëpive diskografike përkatëse.”
          </p>
        </div>
      </section>

      <section className="cta-panel">
        <h2>Gati për të ndezur mikrofonin?</h2>
        <p>
          Zgjidh këngëtarin, përgatit tekstin dhe ftoni ekipin. Katalogu ynë reagon menjëherë ndaj kërkimit dhe filtrimit sipas inicialeve.
        </p>
        <button type="button" className="cta" onClick={onExplore}>
          Filloni këtu
        </button>
      </section>
    </main>
  );
}

function HomeFeed({ songs, onExplore }) {
  const feedNodes = songs.map((song) => <HomeSongCard key={song.videoId} song={song} />);

  if (!songs.length) {
    return null;
  }

  return (
    <section className="home-feed">
      <div className="home-feed__header">
        <div>
          <p className="section-eyebrow">Publikimet më të fundit</p>
          <h3>10 videot më të reja në KendoAL</h3>
        </div>
        <button type="button" className="ghost" onClick={onExplore}>
          Shiko katalogun
        </button>
      </div>
      <div className="home-feed__grid">{feedNodes}</div>
    </section>
  );
}

function HomeSongCard({ song }) {
  return (
    <article className="home-song-card">
      <img src={`https://i.ytimg.com/vi/${song.videoId}/mqdefault.jpg`} alt={song.title || `${song.song} - ${song.singer || ''}`} />
      <div>
        <p className="home-song-card__title">{song.title || song.song}</p>
        <p className="home-song-card__subtitle">{song.published || song.singer}</p>
        <a href={song.url || `https://www.youtube.com/watch?v=${song.videoId}`} target="_blank" rel="noreferrer">
          Hap në YouTube
        </a>
      </div>
    </article>
  );
}

function CatalogView({ karaokeData, totalSongs, totalArtists }) {
  const viewportWidth = useViewportWidth();
  const isMobile = viewportWidth < 768;
  const [query, setQuery] = useState('');
  const [activeLetter, setActiveLetter] = useState('');
  const [activePlayers, setActivePlayers] = useState([]);
  const [blockedVideos, setBlockedVideos] = useState({});
  const [youtubeReady, setYoutubeReady] = useState(false);
  const [visibleCount, setVisibleCount] = useState(10);
  const sentinelRef = useRef(null);

  const filteredSongs = useMemo(() => {
    const lowered = query.toLowerCase();
    return karaokeData.filter((track) => {
      const matchesLetter = activeLetter ? track.singer.toLowerCase().startsWith(activeLetter.toLowerCase()) : true;
      const matchesQuery = lowered
        ? track.singer.toLowerCase().includes(lowered) || track.song.toLowerCase().includes(lowered)
        : true;
      return matchesLetter && matchesQuery;
    });
  }, [karaokeData, query, activeLetter]);

  useEffect(() => {
    setVisibleCount(Math.min(10, filteredSongs.length || 10));
  }, [filteredSongs.length]);

  const visibleSet = useMemo(() => {
    return new Set(filteredSongs.slice(0, visibleCount).map((track) => track.videoId));
  }, [filteredSongs, visibleCount]);

  const groupedBySinger = useMemo(() => {
    const grouped = filteredSongs.reduce((acc, track) => {
      if (!acc[track.singer]) {
        acc[track.singer] = [];
      }
      acc[track.singer].push(track);
      return acc;
    }, {});

    return Object.entries(grouped)
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([singer, songs]) => {
        const filtered = songs
          .sort((a, b) => a.song.localeCompare(b.song))
          .filter((song) => visibleSet.has(song.videoId));
        if (!filtered.length) {
          return null;
        }
        return {
          singer,
          songs: filtered,
        };
      })
      .filter(Boolean);
  }, [filteredSongs, visibleSet]);

  useEffect(() => {
    const visibleIds = new Set(filteredSongs.map((track) => track.videoId));
    setActivePlayers((prev) => prev.filter((id) => visibleIds.has(id)));
  }, [filteredSongs]);

  useEffect(() => {
    setActivePlayers((prev) => prev.filter((id) => visibleSet.has(id)));
  }, [visibleSet]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    if (window.YT && window.YT.Player) {
      setYoutubeReady(true);
    }
  }, []);

  useEffect(() => {
    if (!activePlayers.length || youtubeReady) {
      return;
    }

    if (typeof window === 'undefined') {
      return;
    }

    if (window.YT && window.YT.Player) {
      setYoutubeReady(true);
      return;
    }

    const scriptId = 'youtube-iframe-api';
    if (!document.getElementById(scriptId)) {
      const tag = document.createElement('script');
      tag.id = scriptId;
      tag.src = 'https://www.youtube.com/iframe_api';
      document.body.appendChild(tag);
    }

    const previousReady = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      if (typeof previousReady === 'function') {
        previousReady();
      }
      setYoutubeReady(true);
    };

    const checkInterval = setInterval(() => {
      if (window.YT && window.YT.Player) {
        setYoutubeReady(true);
        clearInterval(checkInterval);
      }
    }, 250);

    return () => clearInterval(checkInterval);
  }, [activePlayers.length, youtubeReady]);

  const handleVideoBlocked = useCallback((videoId, url) => {
    setBlockedVideos((prev) => ({ ...prev, [videoId]: true }));
    setActivePlayers((prev) => prev.filter((id) => id !== videoId));
    window.open(url, '_blank', 'noopener,noreferrer');
  }, []);

  const hasMore = visibleCount < filteredSongs.length;

  useEffect(() => {
    if (!hasMore) {
      return undefined;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting) {
          setVisibleCount((prev) => Math.min(prev + 10, filteredSongs.length));
        }
      },
      { rootMargin: '200px' }
    );
    const current = sentinelRef.current;
    if (current) {
      observer.observe(current);
    }
    return () => {
      if (current) {
        observer.unobserve(current);
      }
    };
  }, [hasMore, filteredSongs.length]);

  return (
    <main className="catalog">
      <section className="catalog-hero">
        <p className="section-eyebrow">Arkiva KendoAL</p>
        <h1>Katalogu i plotë i këngëve karaoke shqip.</h1>
        <p>
          Filtroni sipas inicialeve, kërkoni drejtpërdrejt këngëtarin ose titullin dhe shikoni menjëherë versionin karaoke.
          Videot që bllokohen për embed hapen automatikisht në YouTube që të mos humbisni ritmin.
        </p>
      </section>

      <section className="filters">
        <label className="search-field" htmlFor="karaoke-search">
          <span>Kërko sipas këngëtarit ose këngës</span>
          <input
            id="karaoke-search"
            type="text"
            placeholder="p.sh. Besa, Fishekzjarre, Noizy"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
        </label>
        <div className="alphabet">
          <button
            type="button"
            onClick={() => {
              setActiveLetter('');
              setQuery('');
            }}
            className={!activeLetter ? 'alphabet__letter is-active' : 'alphabet__letter'}
          >
            Të gjitha
          </button>
          {alphabet.map((letter) => {
            const isActive = activeLetter === letter;
            return (
              <button
                key={letter}
                type="button"
                onClick={() => {
                  setActiveLetter(isActive ? '' : letter);
                  setQuery('');
                }}
                className={isActive ? 'alphabet__letter is-active' : 'alphabet__letter'}
              >
                {letter}
              </button>
            );
          })}
        </div>
      </section>

      <div className="catalog-layout">
        <div className="catalog-main">
          <section className="results">
            {groupedBySinger.length === 0 && (
              <p className="results__empty">Asnjë këngë nuk përputhet me kërkimin. Provoni një term tjetër.</p>
            )}

            {groupedBySinger.map(({ singer, songs }) => (
              <section className="singer-block" key={singer}>
                <div className="singer-block__header">
                  <h2>{singer}</h2>
                  <span>
                    {songs.length} {songs.length === 1 ? 'këngë' : 'këngë'}
                  </span>
                </div>
            <div
              className={
                !isMobile
                  ? songs.length === 1
                    ? 'song-grid song-grid--single'
                    : songs.length <= 4
                    ? 'song-grid song-grid--double'
                    : 'song-grid song-grid--multi'
                  : 'song-grid'
              }
            >
              {songs.map((song) => {
                const isActive = activePlayers.includes(song.videoId);
                const blocked = blockedVideos[song.videoId];
                const openExternal = () => window.open(song.url, '_blank', 'noopener,noreferrer');
                const showEmbeddedPlayer = !isMobile;
                const card = (
                  <article
                    className={!isMobile && songs.length === 1 ? 'song-card is-single' : 'song-card'}
                    key={song.videoId}
                  >
                        <div className="song-card__meta">
                      <div>
                        <p className="song-card__title">{song.song}</p>
                        <p className="song-card__subtitle">{singer}</p>
                      </div>
                      {!isMobile && (
                        <a href={song.url} target="_blank" rel="noreferrer">
                          Hap në YouTube
                        </a>
                      )}
                    </div>
                        <div className="song-card__player">
                          {!showEmbeddedPlayer ? (
                            <button
                              type="button"
                              className="song-card__placeholder song-card__external"
                              onClick={openExternal}
                            >
                              <img src={`https://i.ytimg.com/vi/${song.videoId}/hqdefault.jpg`} alt={song.song} />
                              <span className="song-card__play">Shiko në YouTube</span>
                            </button>
                          ) : blocked ? (
                            <button
                              type="button"
                              className="song-card__placeholder song-card__external"
                              onClick={openExternal}
                            >
                              <span className="song-card__play">Shiko në YouTube</span>
                            </button>
                          ) : isActive ? (
                            <SongPlayer
                              videoId={song.videoId}
                              title={song.song}
                              isActive={isActive}
                              ytReady={youtubeReady}
                              onBlocked={() => handleVideoBlocked(song.videoId, song.url)}
                            />
                          ) : (
                            <button
                              type="button"
                              className="song-card__placeholder"
                              onClick={() =>
                                setActivePlayers((prev) =>
                                  prev.includes(song.videoId) ? prev : [...prev, song.videoId]
                                )
                              }
                              aria-label={`Lojëso ${song.song} karaoke`}
                            >
                              <img src={`https://i.ytimg.com/vi/${song.videoId}/hqdefault.jpg`} alt={song.song} />
                              <span className="song-card__play">Luaj karaoke</span>
                            </button>
                          )}
                        </div>
                      </article>
                    );

                    return card;
                  })}
                </div>
              </section>
            ))}
          </section>
        </div>
      </div>
      {hasMore && <div ref={sentinelRef} className="catalog-sentinel" aria-hidden="true" />}
    </main>
  );
}

function DisclaimerPage() {
  return (
    <main className="disclaimer-page">
      <p className="section-eyebrow">Ligjore</p>
      <h1>KLAUZOLA / TË DREJTAT E AUTORIT</h1>
      <p>
        “Kjo faqe shfaq video të hostuara në platformën YouTube përmes funksionit zyrtar ‘embed’. Ne nuk i ngarkojmë,
        nuk i ruajmë dhe nuk i modifikojmë këto video.
      </p>
      <p>
        Të gjitha të drejtat e autorit (audio, tekst, kompozim, video) u përkasin autorëve dhe mbajtësve përkatës të të
        drejtave.
      </p>
      <h2>PËRDORIMI I PËRMBAJTJES</h2>
      <p>
        “Nuk ofrojmë shkarkime (MP3/MP4), nuk ofrojmë kopje të përmbajtjes dhe nuk ndërhyjmë në reklamimin e YouTube.”
      </p>

      <h2>KËRKESË PËR HEQJE / KONTAKT</h2>
      <p>
        “Nëse jeni mbajtës i të drejtave dhe mendoni se një përmbajtje duhet të hiqet ose të korrigjohet, ju lutem na
        kontaktoni me: (1) linkun e faqes dhe (2) linkun e videos në YouTube. Ne do ta shqyrtojmë kërkesën dhe do të
        veprojmë brenda një afati të arsyeshëm.”
      </p>

      <p className="contact-line">Kontakt: kendopikal@gmail.com</p>
    </main>
  );
}

export default App;
